import { useRef, useState } from "react";

/** A committed write is never retried just because the following read failed. */
export function useAcknowledgedMutation(refresh: () => Promise<void>, onFinished: () => void) {
  const pending = useRef(false), wrote = useRef(false);
  const [phase, setPhase] = useState<"writing" | "refreshing" | null>(null);
  const [committed, setCommitted] = useState(false), [error, setError] = useState("");
  const reload = async () => {
    setPhase("refreshing");
    try { await refresh(); onFinished(); }
    catch { setError("처리는 완료됐지만 최신 목록을 불러오지 못했습니다. 상태를 다시 확인해 주세요."); }
  };
  const execute = async (action: () => Promise<unknown>) => {
    if (pending.current || wrote.current) return;
    pending.current = true; setPhase("writing"); setError("");
    try {
      await action(); wrote.current = true; setCommitted(true); await reload();
    } catch (cause) { setError(cause instanceof Error ? cause.message : "처리하지 못했습니다. 다시 시도해 주세요."); }
    finally { pending.current = false; setPhase(null); }
  };
  const retry = async () => {
    if (pending.current || !wrote.current) return;
    pending.current = true; setError("");
    try { await reload(); } finally { pending.current = false; setPhase(null); }
  };
  /** For a mutation hook reused across successive edits (not remounted per item): clear committed/error state once the caller is done acting on it. */
  const reset = () => { wrote.current = false; setCommitted(false); setError(""); };
  return { phase, committed, error, busy: phase !== null, execute, retry, reset };
}
